import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SchemeHeader } from '../../components/Scheme/SchemeHeader';
import { SchemePreviewCard } from '../../components/Scheme/SchemePreviewCard';
import { SchemeSummaryCard } from '../../components/Scheme/SchemeSummaryCard';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSchemeDetails, addScheme, editScheme } from '../../store/schemeSlice';
import { fetchDistributorChannels } from '../../store/distributorChannelSlice';
import type { CreateSchemeForm } from '../../types/scheme';
import { getZonesAPI, getCitiesAPI } from '../../api/locations';
import { getProducts, type ProductItem } from '../../api/products';
import { InfoToast } from '../../components/Common/InfoToast';
import { CustomDropdown } from '../../components/Common/CustomDropdown';
import { CustomCalendar } from '../../components/Common/CustomCalendar';

export const CreateScheme: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, selectedSchemeDetails } = useAppSelector((state) => state.scheme);
  const { channels } = useAppSelector((state) => state.channels);

  const [zones, setZones] = useState<Array<{ id: number; name: string }>>([]);
  const [cities, setCities] = useState<Array<{ id: number; zoneId?: number; name: string }>>([]);
  const [productsList, setProductsList] = useState<ProductItem[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);

  const [formData, setFormData] = useState<CreateSchemeForm>(() => ({
    zone: '',
    zoneId: '',
    city: '',
    cityId: '',
    productId: '',
    productName: '',
    schemeName: '',
    distributorType: 'GT',
    eligibleUnits: '10',
    freeProducts: '1',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  }));

  const [crateMapping, setCrateMapping] = useState('....Units / Crate');

  const filteredCities = useMemo(() => {
    if (!formData.zoneId) return [];
    const targetZoneId = String(formData.zoneId);

    return cities.filter((c: any) => {
      const cZoneId = String(c.zoneId ?? c.zone_id ?? c.territory?.zoneId ?? '');
      return cZoneId === targetZoneId;
    });
  }, [cities, formData.zoneId]);

  useEffect(() => {
    if (!channels || channels.length === 0) {
      dispatch(fetchDistributorChannels());
    }
    // Load Zones, Cities, and Products dropdowns
    const loadDropdowns = async () => {
      try {
        const [zonesRes, citiesRes, prodRes] = await Promise.allSettled([
          getZonesAPI(),
          getCitiesAPI(),
          getProducts({ limit: 100 }),
        ]);

        if (zonesRes.status === 'fulfilled' && zonesRes.value?.data) {
          setZones(zonesRes.value.data);
        }
        if (citiesRes.status === 'fulfilled' && citiesRes.value?.data) {
          setCities(citiesRes.value.data);
        }
        if (prodRes.status === 'fulfilled' && prodRes.value?.data?.products) {
          setProductsList(prodRes.value.data.products);
        }
      } catch (err) {
        console.warn('Error loading dropdowns for scheme creation:', err);
      }
    };
    loadDropdowns();
  }, [dispatch, channels]);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchSchemeDetails(id));
    }
  }, [isEditMode, id, dispatch]);

  const formatDateForInput = (dateVal?: string | number | Date | null): string => {
    if (!dateVal) return '';
    const str = String(dateVal).trim();
    if (!str) return '';

    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      return str;
    }

    if (str.includes('T')) {
      const part = str.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(part)) return part;
    }
    if (str.includes(' ')) {
      const part = str.split(' ')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(part)) return part;
    }

    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  useEffect(() => {
    queueMicrotask(() => {
      if (isEditMode && selectedSchemeDetails) {
        const rawCities = selectedSchemeDetails.citiesList || [];
        const filteredInitCities = rawCities.filter((c) => c !== 'All Cities');
        setSelectedCities(filteredInitCities);

        const rawStart =
          selectedSchemeDetails.startDate ||
          (selectedSchemeDetails as any).start_date ||
          (selectedSchemeDetails.rawBackendData as any)?.startDate ||
          (selectedSchemeDetails.rawBackendData as any)?.start_date ||
          '';

        const rawEnd =
          selectedSchemeDetails.endDate ||
          (selectedSchemeDetails as any).end_date ||
          (selectedSchemeDetails.rawBackendData as any)?.endDate ||
          (selectedSchemeDetails.rawBackendData as any)?.end_date ||
          '';

        setFormData({
          id: selectedSchemeDetails.id,
          zone: selectedSchemeDetails.zone || '',
          city: selectedSchemeDetails.city || filteredInitCities.join(', '),
          productId: String(selectedSchemeDetails.productId || ''),
          productName: selectedSchemeDetails.product || '',
          schemeName: selectedSchemeDetails.name || '',
          distributorType: selectedSchemeDetails.distType || 'GT',
          distributorTypeId: selectedSchemeDetails.distributorTypeId,
          eligibleUnits: String(selectedSchemeDetails.eligibleProducts || '10'),
          freeProducts: String(selectedSchemeDetails.freeProducts || '1'),
          startDate: formatDateForInput(rawStart),
          endDate: formatDateForInput(rawEnd),
          description: selectedSchemeDetails.description,
        });

        if (selectedSchemeDetails.crateMapping) {
          setCrateMapping(selectedSchemeDetails.crateMapping);
        }
      }
    });
  }, [isEditMode, selectedSchemeDetails]);

  useEffect(() => {
    if (channels.length > 0) {
      if (formData.distributorTypeId) {
        const match = channels.find((ch) => ch.id === formData.distributorTypeId);
        if (match && match.name && match.name !== formData.distributorType) {
          queueMicrotask(() => {
            setFormData((prev) => ({ ...prev, distributorType: match.name }));
          });
        }
      } else if (formData.distributorType) {
        const typeStr = String(formData.distributorType).toUpperCase();
        const match = channels.find(
          (ch) => (ch.name && ch.name.toUpperCase() === typeStr) || String(ch.id) === typeStr
        );
        if (match) {
          queueMicrotask(() => {
            setFormData((prev) => ({
              ...prev,
              distributorTypeId: match.id,
              distributorType: match.name || String(formData.distributorType),
            }));
          });
        }
      }
    }
  }, [channels, formData.distributorTypeId, formData.distributorType]);

  const handleInputChange = (
    field: keyof CreateSchemeForm,
    value: string | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleZoneChange = (zoneIdStr: string) => {
    const selectedZoneObj = zones.find((z) => String(z.id) === zoneIdStr);
    setSelectedCities([]);
    setFormData((prev) => ({
      ...prev,
      zoneId: zoneIdStr,
      zone: selectedZoneObj ? selectedZoneObj.name : '',
      city: '',
      cityId: '',
      cityIds: [],
    }));
  };

  const handleMultiCityChange = (newSelectedCities: string[]) => {
    setSelectedCities(newSelectedCities);

    const actualSelected = newSelectedCities.filter(
      (name) => !name.startsWith('Select All') && name !== 'All Cities (Global)'
    );

    if (actualSelected.length > 0) {
      const selectedCityObjs = filteredCities.filter((c) => actualSelected.includes(c.name));
      const cityIdsArr = selectedCityObjs.map((c) => Number(c.id)).filter((n) => !isNaN(n));
      setFormData((prev) => ({
        ...prev,
        city: actualSelected.join(', '),
        cityId: cityIdsArr.join(','),
        cityIds: cityIdsArr,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        city: '',
        cityId: '',
        cityIds: [],
      }));
    }
  };

  const handleRemoveCity = (cityToRemove: string) => {
    const updated = selectedCities.filter(
      (c) => c !== cityToRemove && !c.startsWith('Select All') && c !== 'All Cities (Global)'
    );
    handleMultiCityChange(updated);
  };

  const handleProductSelectByName = (productNameStr: string) => {
    if (productNameStr === 'All Products' || !productNameStr) {
      setCrateMapping('12 Units / Crate');
      setFormData((prev) => ({
        ...prev,
        productId: '',
        productName: 'All Products',
        crateUnits: undefined,
      }));
      return;
    }

    const selectedProd = productsList.find((p) => p.name === productNameStr);
    const productIdStr = selectedProd ? String(selectedProd.id) : '';
    const name = selectedProd ? selectedProd.name : productNameStr;

    const units = selectedProd?.units_per_case ?? selectedProd?.crate?.units ?? 12;
    const mapping = `${units} Units / Crate`;

    setCrateMapping(mapping);
    setFormData((prev) => ({
      ...prev,
      productId: productIdStr,
      productName: name,
      crateUnits: units,
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!formData.schemeName.trim()) {
      alert('Please enter a Scheme Name');
      return;
    }

    try {
      if (isEditMode) {
        await dispatch(editScheme(formData)).unwrap();
      } else {
        await dispatch(addScheme(formData)).unwrap();
      }
      navigate('/scheme');
    } catch (err: any) {
      alert(err || 'Failed to save scheme');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Top Navigation Header */}
      <SchemeHeader
        title={isEditMode ? 'Edit Scheme' : 'Create Scheme'}
        showBack={true}
        showActions={!isEditMode}
        onBackClick={() => navigate('/scheme')}
        onCreateClick={handleSubmit}
      />

      {/* Warning Banner for Active Scheme Modification (Matching Edit Scheme Screenshot) */}
      {isEditMode && (
        <div className="mb-6 bg-red-50 border border-red-200/60 rounded-2xl p-4 flex items-start gap-3 text-xs md:text-sm">
          <div className="text-red-500 mt-0.5 shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <div className="font-bold text-[#B71C1C] text-xs md:text-sm">
              Active Scheme Modification
            </div>
            <div className="text-gray-600 text-xs mt-0.5">
              Changes to an active scheme may affect ongoing distributor eligibility and current inventory allocations.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Top Grid Section: Scheme Details + Live Scheme Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Card 1: Scheme Details */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-0.5">
              Scheme Details
            </h2>
            <p className="text-xs text-gray-400 font-medium mb-5">
              Basic information and target products.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Zone */}
              {/* <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Zone
                </label>
                <div className="relative">
                  <select
                    value={formData.zoneId || ''}
                    onChange={(e) => handleZoneChange(e.target.value)}
                    className="w-full bg-white border border-gray-300 focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3 pr-8 appearance-none transition-colors cursor-pointer"
                  >
                    <option value="">All Zones (Global)</option>
                    {zones.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div> */}

              {/* City */}
              {/* <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  City
                </label>
                <div className="relative">
                  <select
                    value={formData.cityId || ''}
                    onChange={(e) => handleCityChange(e.target.value)}
                    className="w-full bg-white border border-gray-300 focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3 pr-8 appearance-none transition-colors cursor-pointer"
                  >
                    <option value="">All Cities (Global)</option>
                    {cities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div> */}
               {/* Scheme Name * */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Scheme Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Summer Monsoon Bonanza"
                  value={formData.schemeName}
                  onChange={(e) => handleInputChange('schemeName', e.target.value)}
                  required
                  className="w-full bg-white border border-gray-300 focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3 transition-colors"
                />
              </div>

              {/* Distributor type */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Distributor type
                </label>
                <div className="relative">
                  <select
                    value={formData.distributorTypeId ? String(formData.distributorTypeId) : ''}
                    onChange={(e) => {
                      const selectedId = Number(e.target.value);
                      const matchedChan = channels.find((ch) => ch.id === selectedId);
                      setFormData((prev) => ({
                        ...prev,
                        distributorTypeId: selectedId || undefined,
                        distributorType: matchedChan ? matchedChan.name : '',
                      }));
                    }}
                    className="w-full bg-white border border-gray-300 focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3 pr-8 appearance-none transition-colors cursor-pointer"
                  >
                    <option value="">Select Channel</option>
                    {channels.length > 0 ? (
                      channels.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          {ch.name || `Channel #${ch.id}`}
                        </option>
                      ))
                    ) : (
                      <option value="">No Data</option>
                    )}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Product */}
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="block text-xs font-semibold text-gray-700">
                    Product
                  </span>
                  <span className="bg-[#1E88E5] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                    AUTO-CRATE MAPPING
                  </span>
                </div>
                <CustomDropdown
                  placeholder="All Products"
                  options={['All Products', ...productsList.map((p) => p.name)]}
                  value={formData.productName || 'All Products'}
                  onChange={handleProductSelectByName}
                  searchable={true}
                  isMulti={false}
                />

                {/* Auto-Crate Lock Info Box (Matching Edit Scheme Screenshot) */}
                <div className="mt-2 bg-blue-50/70 border border-blue-100 rounded-xl px-3 py-2 flex items-center gap-2 text-xs text-blue-900 font-medium">
                  <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>{crateMapping}</span>
                </div>
              </div>

             
            </div>
          </div>

          {/* Card 2: Scheme Preview */}
          <div className="lg:col-span-1">
            <SchemePreviewCard
              eligibleUnits={formData.eligibleUnits}
              freeProducts={formData.freeProducts}
              unitType="units"
            />
          </div>
        </div>

        {/* Middle Section: Benefit Structure & Validity & Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Card 3: Benefit Structure */}
          <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4">
              Benefit Structure
            </h3>

            <div className="space-y-4">
              {/* Eligible Units * */}
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <label className="block text-xs font-semibold text-gray-700">
                    Eligible Units <span className="text-red-500">*</span>
                  </label>
                  <InfoToast message="Minimum quantity required to qualify for scheme benefit" />
                </div>
                <div className="relative flex items-center border border-gray-300 focus-within:border-[#B71C1C] focus-within:ring-1 focus-within:ring-[#B71C1C] rounded-xl overflow-hidden bg-white">
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g., 10"
                    value={formData.eligibleUnits}
                    onChange={(e) => handleInputChange('eligibleUnits', e.target.value)}
                    required
                    className="w-full py-2.5 px-3 text-xs md:text-sm text-gray-800 outline-none bg-transparent"
                  />
                  <div className="bg-white text-gray-600 border-l border-gray-300 px-4 py-2.5 text-xs md:text-sm font-medium shrink-0">
                    Units
                  </div>
                </div>
              </div>

              {/* Number of Free Products * */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Number of Free Products <span className="text-red-500">*</span>
                </label>
                <div className="relative flex items-center border border-gray-300 focus-within:border-[#B71C1C] focus-within:ring-1 focus-within:ring-[#B71C1C] rounded-xl overflow-hidden bg-white">
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g., 1"
                    value={formData.freeProducts}
                    onChange={(e) => handleInputChange('freeProducts', e.target.value)}
                    required
                    className="w-full py-2.5 px-3 text-xs md:text-sm text-gray-800 outline-none bg-transparent"
                  />
                  <div className="bg-white text-gray-600 border-l border-gray-300 px-4 py-2.5 text-xs md:text-sm font-medium shrink-0">
                    Units
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 4: Validity & Status */}
          <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4">
              Validity & Status
            </h3>

            <div className="space-y-4">
              <CustomCalendar
                label="Start Date"
                required
                value={formData.startDate}
                onChange={(dateStr) => handleInputChange('startDate', dateStr)}
                placeholder="Select Start Date (dd/mm/yyyy)"
              />

              <CustomCalendar
                label="End Date"
                required
                value={formData.endDate}
                minDate={formData.startDate}
                onChange={(dateStr) => handleInputChange('endDate', dateStr)}
                placeholder="Select End Date (dd/mm/yyyy)"
              />
            </div>
          </div>
        </div>
                 
        <div className='bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm'>
          <h3 className='text-base md:text-lg font-bold text-gray-900 mb-4'>Select City</h3>
          {/* Zone */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='w-full'>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Zone
              </label>
              <div className="relative">
                <select
                  value={formData.zoneId || ''}
                  onChange={(e) => handleZoneChange(e.target.value)}
                  className="w-full bg-white border border-gray-300 focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3 pr-8 appearance-none transition-colors cursor-pointer"
                >
                  <option value="">All Zones (Global)</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* City Multiselect */}
            <div className='w-full'>
              <CustomDropdown
                label="City"
                placeholder={formData.zoneId ? "Select Cities" : "Select Zone First"}
                options={formData.zoneId ? ['Select All Cities', ...filteredCities.map((c) => c.name)] : []}
                value={selectedCities}
                onChange={handleMultiCityChange}
                isMulti={true}
                searchable={true}
                showOkButton={true}
                unitLabel="Cities"
              />
            </div>
          </div>
        </div> 

        {/* Bottom Section: Scheme Summary & Form Actions */}
        <SchemeSummaryCard
          productName={formData.productName}
          eligibleUnits={formData.eligibleUnits}
          freeProducts={formData.freeProducts}
          city={formData.city}
          crateMapping={crateMapping}
          onRemoveCity={handleRemoveCity}
          onCancel={() => navigate('/scheme')}
          onSubmit={handleSubmit}
          isSubmitting={loading}
          submitButtonText={isEditMode ? 'Save Changes' : 'Create Scheme'}
        />
      </form>
    </div>
  );
};
